// src/components/layout/MainLayout.jsx
import { Outlet, useLocation, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import {
  Menu,
  ChevronDown,
  User,
  Settings,
  LogOut,
  LayoutDashboard,
  Instagram,
  Gift,
  Star,
  Users,
  X,
  Lock,
} from 'lucide-react';
import logoPsiando from '@/assets/icon/logo_psiando.jpeg';
import { NotificationContainer } from '@/components/ui/NotificationContainer';

// =============================================================================
// LOGO — imagem real, proporção mantida, sem distorção
// =============================================================================
const Logo = ({ size = 48 }) => {
  const dim = typeof size === 'string' ? parseInt(size, 10) : size;
  return (
    <div style={{
      width: dim, height: dim,
      borderRadius: '50%',
      overflow: 'hidden',
      border: '3px solid var(--primary-accent)',
      boxShadow: '0 4px 16px rgba(26,92,88,0.25)',
      flexShrink: 0,
    }}>
      <img
        src={logoPsiando}
        alt="Psiando"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
};

// =============================================================================
// UTILITÁRIOS
// =============================================================================
const getThemeClass = (userType) => {
  const themes = {
    psicologo:    'theme-psicologo',
    supervisor:   'theme-supervisor',
    instituicao:  'theme-instituicao',
    internacional:'theme-internacional',
  };
  return themes[userType] || 'theme-psicologo';
};

// =============================================================================
// FOOTER — ESTÁTICO E DEFINITIVO
//
// ✔ 4 colunas idênticas ao original: Serviços, Empresa, Legal, Suporte
// ✔ Empresa: Sobre Nós, Contato, Preços, Blog — todos mantidos
// ✔ Links com `to` navegam via <Link>
// ✔ Links com `badge:'Em breve'` e `to:null` mostram badge mas NÃO bloqueiam layout
// ✔ Legal aponta para páginas reais /privacy /terms /lgpd /codigo-etica
// =============================================================================

const FOOTER_SECTIONS = [
  {
    title: 'Serviços',
    links: [
      { label: 'Supervisão Clínica',   to: '/find-supervisor' },
      { label: 'Agenda Digital',       to: null,    onTrial: true },
      { label: 'Para Instituições',    to: null,    badge: 'Em breve' },
      { label: 'Matching Inteligente', to: '/about' },
    ],
  },
  {
    title: 'Empresa',
    links: [
      { label: 'Sobre Nós', to: '/about'   },
      { label: 'Contato',   to: '/contact' },
      { label: 'Preços',    to: '/pricing' },
      { label: 'Blog',      to: null, badge: 'Em breve' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Política de Privacidade', to: '/privacy'      },
      { label: 'Termos de Uso',           to: '/terms'        },
      { label: 'LGPD',                    to: '/lgpd'         },
      { label: 'Código de Ética',         to: '/codigo-etica' },
    ],
  },
];

const SOCIAL_LINKS = [
  { icon: Instagram, href: 'https://www.instagram.com/psiandosupervisao/', label: 'Instagram' },
];

// Badge "Em breve" inline — discreto, sem bloquear layout
const ComingSoonBadge = () => (
  <span
    className="ml-2 text-xs font-medium px-1.5 py-0.5 rounded-full"
    style={{
      backgroundColor: 'rgba(42,157,143,0.20)',
      color: 'var(--secondary-light)',
    }}
  >
    Em breve
  </span>
);

// ─── Dialog de Trial (sem dependência de auth) ────────────────────────────────
function TrialInfoDialog({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between" style={{ borderBottom: '1px solid #f0faf8' }}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #e6f7f5, #b2ebe0)' }}
            >
              <Gift size={20} style={{ color: '#1a7a6e' }} />
            </div>
            <div>
              <span
                className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-0.5"
                style={{ backgroundColor: '#e6f7f5', color: '#1a7a6e' }}
              >
                Trial Gratuito por 30 dias
              </span>
              <h3 className="text-base font-bold text-gray-900 leading-tight">
                Desbloqueie Chat Bot/IA e Agenda Digital grátis
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors ml-2 flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600 leading-relaxed">
            Convide <strong className="text-gray-800">10 colegas psicólogos</strong> para se
            cadastrar na Psiando e ganhe acesso completo às funcionalidades bloqueadas por{' '}
            <strong className="text-gray-800">30 dias</strong> — sem cartão de crédito e sem
            compromisso.
          </p>

          <div className="space-y-3">
            {[
              { icon: <Star size={14} />, title: 'Cadastre-se gratuitamente', desc: 'Crie sua conta no plano Multi Psi.' },
              { icon: <Users size={14} />, title: 'Convide 10 colegas', desc: 'Compartilhe seu link exclusivo pelo WhatsApp, e-mail ou redes sociais.' },
              { icon: <Gift size={14} />, title: 'Acesso liberado automaticamente', desc: 'Assim que os 10 convites forem aceitos, o trial de 30 dias começa.' },
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: 'linear-gradient(135deg, #1a7a6e, #2a9d8f)', color: '#fff' }}
                >
                  {step.icon}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{step.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div
            className="flex items-start gap-2 rounded-xl p-3 text-xs text-gray-500"
            style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}
          >
            <Lock size={12} className="mt-0.5 flex-shrink-0" style={{ color: '#94a3b8' }} />
            <span>
              O trial cobre <strong className="text-gray-700">Chat Bot/IA</strong> e{' '}
              <strong className="text-gray-700">Agenda Digital</strong>. Funcionalidades do plano
              base permanecem disponíveis normalmente após o período.
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3">
          <a
            href="https://psiando.com.br/register"
            className="flex-1 text-center font-semibold py-2.5 px-5 rounded-xl text-sm text-white transition-all duration-200 hover:opacity-90 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #1a7a6e, #2a9d8f)' }}
          >
            Quero experimentar grátis
          </a>
          <button
            onClick={onClose}
            className="flex-1 text-center font-semibold py-2.5 px-5 rounded-xl text-sm text-gray-600 border border-gray-200 bg-white transition-all duration-200 hover:bg-gray-50"
          >
            Agora não
          </button>
        </div>
      </div>
    </div>
  );
}

// Renderiza link ativo, item clicável (onTrial) ou item sem destino com badge
const FooterLink = ({ link, onTrialClick }) => {
  if (link.to) {
    return (
      <Link
        to={link.to}
        className="inline-flex items-center text-slate-300 hover:text-white transition-colors text-sm"
      >
        {link.label}
        {link.badge && <ComingSoonBadge />}
      </Link>
    );
  }
  if (link.onTrial) {
    return (
      <button
        type="button"
        onClick={onTrialClick}
        className="inline-flex items-center text-slate-300 hover:text-white transition-colors text-sm"
      >
        {link.label}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center text-slate-400 text-sm">
      {link.label}
      {link.badge && <ComingSoonBadge />}
    </span>
  );
};

const Footer = () => {
  const [trialDialogOpen, setTrialDialogOpen] = useState(false);

  return (
  <>
  <footer className="bg-slate-900 text-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center mb-6 gap-4">
              <Logo size={64} />
              <h3 className="text-2xl font-bold">Psiando</h3>
            </div>
            <p className="text-slate-300 mb-6 leading-relaxed">
              A primeira plataforma nacional de matching inteligente para supervisão clínica,
              conectando psicólogos com supervisores qualificados.
            </p>
            <div className="flex space-x-4 mb-6">
              {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center transition-colors group"
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--primary-accent)')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                >
                  <Icon className="w-5 h-5 text-slate-400 group-hover:text-white" />
                </a>
              ))}
            </div>
            <div className="border-t border-slate-700 pt-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Responsáveis Técnicos</p>
              <p className="text-sm text-slate-300 leading-relaxed">
                Hermelany Mª Luna Antunes<br />
                <span className="text-slate-400 text-xs">CRP: 02/26332</span>
              </p>
              <p className="text-sm text-slate-300 leading-relaxed mt-2">
                Victória Caroline de França Torres<br />
                <span className="text-slate-400 text-xs">CRP: 02/27470</span>
              </p>
            </div>
          </div>

          {/* 4 colunas de links — grid idêntico ao original */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {FOOTER_SECTIONS.map(({ title, links }) => (
                <div key={title}>
                  <h4 className="text-lg font-semibold mb-6 text-white">{title}</h4>
                  <ul className="space-y-3">
                    {links.map((link) => (
                      <li key={link.label}>
                        <FooterLink link={link} onTrialClick={() => setTrialDialogOpen(true)} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Barra inferior */}
      <div className="border-t border-slate-800 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} Psiando. Todos os direitos reservados.
          </p>
          <div className="flex items-center space-x-6 mt-4 md:mt-0">
            <span className="text-slate-400 text-sm">Feito com ❤️ no Brasil</span>
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: 'var(--primary-accent)' }}
            />
            <span className="text-slate-400 text-sm">Sistema seguro LGPD</span>
          </div>
        </div>
      </div>
    </div>
    </footer>

      {trialDialogOpen && (
        <TrialInfoDialog onClose={() => setTrialDialogOpen(false)} />
      )}
    </>
  );
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export const MainLayout = () => {
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const { notifications } = useApp();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isScrolled, setIsScrolled]   = useState(false);

  const isDashboardRoute         = location.pathname.startsWith('/dashboard');
  const isPublicConsultorioRoute = location.pathname.startsWith('/consultorio/');
  const isAgendamentoRoute       = location.pathname.includes('/agendamento/');
  const AUTH_PATHS = ['/login', '/register', '/forgot-password', '/reset-password'];
  const isAuthRoute              = AUTH_PATHS.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (userProfile?.userType) {
      const themeClass = getThemeClass(userProfile.userType);
      document.body.className = document.body.className.replace(/theme-\w+/g, '');
      document.body.classList.add(themeClass);
    }
    return () => {
      document.body.className = document.body.className.replace(/theme-\w+/g, '');
    };
  }, [userProfile]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  const getLayoutClass = () => {
    if (isAuthRoute)                                    return 'auth-layout';
    if (isPublicConsultorioRoute || isAgendamentoRoute) return 'consultorio-layout';
    if (isDashboardRoute)                               return 'dashboard-layout';
    return 'public-layout';
  };

  if (isDashboardRoute) return <Outlet />;

  return (
    <div className={`min-h-screen flex flex-col ${getLayoutClass()}`}>
      {!isAuthRoute && (
        <Header
          isScrolled={isScrolled}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          showDashboardNav={false}
          user={user}
        />
      )}

      <div className="flex flex-1">
        <main
          className={`flex-1 transition-all duration-300 ${
            isPublicConsultorioRoute ? 'bg-gray-50' : ''
          }`}
          id="main-content"
        >
          <Outlet />
        </main>
      </div>

      {!isDashboardRoute && !isPublicConsultorioRoute && !isAgendamentoRoute && !isAuthRoute && (
        <Footer />
      )}

      <NotificationContainer notifications={notifications} />
    </div>
  );
};

// =============================================================================
// HEADER PÚBLICO
// =============================================================================
const NavLink = ({ to, current, children }) => (
  <Link
    to={to}
    className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200"
    style={
      current
        ? { color: 'var(--primary-accent)', backgroundColor: 'var(--primary-50)' }
        : { color: '#334155' }
    }
    onMouseEnter={e => { if (!current) e.currentTarget.style.color = 'var(--primary-accent)'; }}
    onMouseLeave={e => { if (!current) e.currentTarget.style.color = '#334155'; }}
  >
    {children}
  </Link>
);

const AuthButtons = () => (
  <div className="flex items-center space-x-3">
    <Link
      to="/login"
      className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-slate-700"
      onMouseEnter={e => (e.currentTarget.style.color = 'var(--primary-accent)')}
      onMouseLeave={e => (e.currentTarget.style.color = '')}
    >
      Entrar
    </Link>
    <Link
      to="/register"
      className="text-white px-6 py-2 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm hover:shadow-md"
      style={{ backgroundColor: 'var(--primary-main)' }}
      onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--primary-accent)')}
      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--primary-main)')}
    >
      Cadastrar
    </Link>
  </div>
);

const UserMenu = ({ user, logout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const displayName =
    user.user_metadata?.name ?? user.user_metadata?.full_name ?? user.email ?? 'Usuário';
  const initials = displayName.charAt(0).toUpperCase();

  const handleLogout = async () => {
    try {
      setIsOpen(false);
      await logout();
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-50 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
          style={{ backgroundColor: 'var(--primary-main)' }}
        >
          <span className="text-white text-sm font-medium">{initials}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 z-50">
            <div className="py-2">
              <div className="px-4 py-3 border-b border-slate-200">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
              </div>
              <Link to="/dashboard"               className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors" onClick={() => setIsOpen(false)}><LayoutDashboard className="w-4 h-4 mr-3" />Dashboard</Link>
              <Link to="/dashboard/perfil"        className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors" onClick={() => setIsOpen(false)}><User className="w-4 h-4 mr-3" />Perfil</Link>
              <Link to="/dashboard/configuracoes" className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors" onClick={() => setIsOpen(false)}><Settings className="w-4 h-4 mr-3" />Configurações</Link>
              <div className="border-t border-slate-200 mt-2 pt-2">
                <button onClick={handleLogout} className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                  <LogOut className="w-4 h-4 mr-3" />Sair
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DashboardNav = () => {
  const { userProfile } = useAuth();
  const location = useLocation();
  const items = [{ to: '/dashboard', label: 'Visão Geral' }];
  if (userProfile?.canCreateAgenda)
    items.push({ to: '/dashboard/agenda-digital/gerenciar', label: 'Agenda Digital' });
  if (userProfile?.canSeekSupervision)
    items.push({ to: '/dashboard/supervisao/minhas', label: 'Supervisões' });
  if (userProfile?.userType === 'supervisor')
    items.push({ to: '/dashboard/meus-supervisionados', label: 'Supervisionados' });
  if (userProfile?.userType === 'instituicao')
    items.push({ to: '/dashboard/instituicao/profissionais', label: 'Profissionais' });

  return (
    <div className="flex items-center space-x-6">
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} current={location.pathname === item.to}>
          {item.label}
        </NavLink>
      ))}
    </div>
  );
};

const Header = ({ isScrolled, sidebarOpen, setSidebarOpen, showDashboardNav, user }) => {
  const { logout } = useAuth();
  const location   = useLocation();

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-200 border-b border-slate-200 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-lg' : 'bg-white'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          <div className="flex items-center">
            {showDashboardNav && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100 mr-3 transition-colors"
                aria-label="Abrir menu"
              >
                <Menu className="w-6 h-6" />
              </button>
            )}
            <Link to="/" className="flex items-center gap-3 group">
              <Logo size={56} />
              <h1
                className="text-2xl font-bold text-slate-900 transition-colors"
                onMouseEnter={e => (e.currentTarget.style.color = 'var(--primary-accent)')}
                onMouseLeave={e => (e.currentTarget.style.color = '')}
              >
                Psiando Global
              </h1>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center space-x-8" aria-label="Navegação principal">
            {!showDashboardNav ? (
              <>
                <NavLink to="/"                current={location.pathname === '/'}>Início</NavLink>
                <NavLink to="/find-supervisor" current={location.pathname === '/find-supervisor'}>Encontrar Supervisor</NavLink>
                <NavLink to="/about"           current={location.pathname === '/about'}>Sobre</NavLink>
                <NavLink to="/pricing"         current={location.pathname === '/pricing'}>Preços</NavLink>
                <NavLink to="/contact"         current={location.pathname === '/contact'}>Contato</NavLink>
              </>
            ) : (
              <DashboardNav />
            )}
          </nav>

          <div className="flex items-center space-x-4">
            {user ? <UserMenu user={user} logout={logout} /> : <AuthButtons />}
          </div>
        </div>
      </div>
    </header>
  );
};

export default MainLayout;